import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DashboardShell } from '@/components/layout/DashboardShell';
import { Card } from '@/components/ui/Card';
import { adminService } from '@/services/admin';
import type { TeacherAttendanceTrendResponse } from '@/services/admin';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { useConfig } from '@/context/ConfigContext';
import { resolveAssetUrl } from '@/utils/assetUrl';
import { TaleemXMark, TaleemXWordmark, isDefaultBranding } from '@/components/brand';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { config } = useConfig();

  // 1. Overview Stats (For Total Registered Students)
  const { data: overviewData, isLoading: isLoadingOverview } = useQuery({
    queryKey: ['admin-overview'],
    queryFn: adminService.fetchOverview,
  });

  // 2. Teachers List (For Total Registered Teachers Count)
  const { data: teachersList, isLoading: isLoadingTeachersList } = useQuery({
    queryKey: ['admin-teachers-list'],
    queryFn: adminService.listTeachers,
  });

  // 3. Students List (For Total Registered Students Count)
  const { data: studentsList, isLoading: isLoadingStudentsList } = useQuery({
    queryKey: ['admin-students-list'],
    queryFn: adminService.listStudents,
  });

  // 4. Today's School Attendance
  const { data: todaySummary, isLoading: isLoadingToday } = useQuery({
    queryKey: ['admin-school-attendance-daily'],
    queryFn: () => adminService.getSchoolAttendanceSummary({ period: 'daily' }),
  });

  // 4. Today's Teacher Attendance
  const { data: teacherDailySummary, isLoading: isLoadingTeacherDaily } = useQuery({
    queryKey: ['admin-teacher-attendance-daily'],
    queryFn: () => adminService.getTeacherAttendanceSummary({ period: 'daily' }),
  });

  // 5. Monthly School Summary (For Average Attendance)
  const { data: monthlySummary, isLoading: isLoadingMonthly } = useQuery({
    queryKey: ['admin-school-attendance-monthly'],
    queryFn: () => adminService.getSchoolAttendanceSummary({ period: 'monthly' }),
  });

  // 6. Teachers Summary (For Total Teachers - Monthly)
  const { data: teacherSummary, isLoading: isLoadingTeachers } = useQuery({
    queryKey: ['admin-teacher-attendance-monthly'],
    queryFn: () => adminService.getTeacherAttendanceSummary({ period: 'monthly' }),
  });

  const getPercentage = (value: number, total: number) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
  };

  const totalStudents = overviewData?.students?.total
    ?? studentsList?.filter((student) => student.status === 'Active').length
    ?? 0;
  const totalTeachers = teachersList?.filter((teacher) => teacher.status === 'Active').length ?? 0;

  // Calculate dynamic month name
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });





  return (
    <DashboardShell title="Dashboard" role="admin">
      {/* 1. Header with Branding */}
      <div className="mb-6 sm:mb-8 flex items-center gap-3 sm:gap-4 border-b border-slate-200 pb-4 sm:pb-6">
        <div className="h-12 w-12 sm:h-16 sm:w-16 shrink-0 overflow-hidden rounded-full bg-white shadow-sm border border-slate-100 flex items-center justify-center">
          {isDefaultBranding(config) ? (
            <TaleemXMark size="md" className="h-8 w-8 sm:h-10 sm:w-10" />
          ) : (
            <img
              src={resolveAssetUrl(config?.school_logo_url) || '/logo-mark.svg'}
              alt={config?.school_name || 'School Logo'}
              className="h-full w-full object-contain p-1"
            />
          )}
        </div>
        <div className="min-w-0">
          {isDefaultBranding(config) ? (
            <TaleemXWordmark size="lg" tone="dark" />
          ) : (
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 truncate">
              {config?.school_name}
            </h1>
          )}
          <p className="text-xs sm:text-sm font-medium text-slate-500">School OS Dashboard</p>
        </div>
      </div>

      <div className="space-y-6 sm:space-y-8">

        {/* 2. Central Highlight: Today's Attendance (Students & Teachers) */}
        <div className="space-y-4 sm:space-y-6">
          <h3 className="text-lg sm:text-xl font-bold text-slate-800 text-center flex items-center justify-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            Today's School Attendance
          </h3>
          <div className="flex flex-col md:flex-row justify-center items-center gap-6 sm:gap-8">
            {/* Student Attendance Highlight */}
            <div className="relative group cursor-pointer transition-transform hover:scale-105" onClick={() => navigate('/admin/attendance')}>
              <div className="absolute inset-0 rounded-full blur-xl bg-blue-500/20 group-hover:bg-blue-500/30 transition-colors"></div>
              <div className="relative flex flex-col items-center justify-center rounded-full bg-white border-4 border-blue-50 shadow-xl w-56 h-56 sm:w-64 sm:h-64 p-4 sm:p-6 text-center">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-slate-400 mb-1 sm:mb-2">Students Present</p>
                <div className="flex flex-col items-center">
                  <h2 className="text-4xl sm:text-5xl font-extrabold text-blue-600 tracking-tight">
                    {todaySummary?.summary?.present ?? 0}
                  </h2>
                  <div className="h-1 w-10 sm:w-12 bg-slate-100 my-1.5 sm:my-2 rounded-full"></div>
                  <p className="text-base sm:text-lg font-bold text-slate-400">
                    {isLoadingOverview || isLoadingToday ? '…' : totalStudents}
                  </p>
                </div>
                <p className="text-xs font-bold text-blue-500 mt-1.5 sm:mt-2 bg-blue-50 px-2 py-0.5 rounded-full">
                  {getPercentage(todaySummary?.summary?.present ?? 0, totalStudents)}%
                </p>
              </div>
            </div>

            {/* Teacher Attendance Highlight */}
            <div className="relative group cursor-pointer transition-transform hover:scale-105" onClick={() => navigate('/admin/attendance/teachers')}>
              <div className="absolute inset-0 rounded-full blur-xl bg-amber-500/20 group-hover:bg-amber-500/30 transition-colors"></div>
              <div className="relative flex flex-col items-center justify-center rounded-full bg-white border-4 border-amber-50 shadow-xl w-56 h-56 sm:w-64 sm:h-64 p-4 sm:p-6 text-center">
                {isLoadingTeacherDaily || isLoadingTeachersList ? (
                  <div className="animate-pulse h-16 w-32 bg-slate-200 rounded"></div>
                ) : (
                  <>
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-slate-400 mb-1 sm:mb-2">Teachers Present</p>
                    <div className="flex flex-col items-center">
                      <h2 className="text-4xl sm:text-5xl font-extrabold text-amber-600 tracking-tight">
                        {teacherDailySummary?.summary?.present ?? 0}
                      </h2>
                      <div className="h-1 w-10 sm:w-12 bg-slate-100 my-1.5 sm:my-2 rounded-full"></div>
                      <p className="text-base sm:text-lg font-bold text-slate-400">
                        {isLoadingTeacherDaily || isLoadingTeachersList ? '…' : totalTeachers}
                      </p>
                    </div>
                    <p className="text-xs font-bold text-amber-500 mt-1.5 sm:mt-2 bg-amber-50 px-2 py-0.5 rounded-full">
                      {getPercentage(teacherDailySummary?.summary?.present ?? 0, totalTeachers)}%
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 3. Detailed Stats Sections */}
        <div className="grid md:grid-cols-2 gap-8">

          {/* Students Box */}
          <div className="bg-slate-50/50 rounded-xl p-6 border border-slate-100">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <span className="p-1 rounded bg-blue-100 text-blue-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </span>
              Students Overview
            </h3>
            <div className="space-y-4">
              {/* Total Registered Students */}
              <div onClick={() => navigate('/admin/records/students')} className="cursor-pointer transition-transform hover:scale-[1.02]">
                <Card className="border-l-4 border-l-blue-500" title="">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Active students</p>
                      <h3 className="text-2xl font-bold text-slate-900 mt-1">
                        {isLoadingOverview ? '...' : totalStudents}
                      </h3>
                    </div>
                    <div className="rounded-full bg-blue-50 p-3">
                      <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center text-xs text-slate-500 font-medium group-hover:text-blue-600 transition-colors">
                    View Student Records →
                  </div>
                </Card>
              </div>

              {/* Student Average Attendance */}
              <div onClick={() => navigate('/admin/attendance')} className="cursor-pointer transition-transform hover:scale-[1.02]">
                <Card className="border-l-4 border-l-emerald-500" title="">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">{currentMonth} Attendance</p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <h3 className="text-2xl font-bold text-slate-900">
                          {isLoadingMonthly ? '...' : `${getPercentage(monthlySummary?.summary?.present || 0, monthlySummary?.summary?.expected_records || monthlySummary?.summary?.total_records || 1)}%`}
                        </h3>
                        <span className="text-sm text-slate-500">
                          {isLoadingMonthly ? '...' : `(${monthlySummary?.summary?.present || 0}/${monthlySummary?.summary?.expected_records || monthlySummary?.summary?.total_records || 0})`}
                        </span>
                      </div>
                    </div>
                    <div className="rounded-full bg-emerald-50 p-3">
                      <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center text-xs text-slate-500 font-medium group-hover:text-emerald-600 transition-colors">
                    View Attendance Analytics →
                  </div>
                </Card>
              </div>
            </div>
          </div>

          {/* Teachers Box */}
          <div className="bg-slate-50/50 rounded-xl p-6 border border-slate-100">
            <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <span className="p-1 rounded bg-amber-100 text-amber-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2-2v10a2 2 0 002 2z" />
                </svg>
              </span>
              Teachers Overview
            </h3>
            <div className="space-y-4">
              {/* Total Registered Teachers */}
              <div onClick={() => navigate('/admin/records/teachers')} className="cursor-pointer transition-transform hover:scale-[1.02]">
                <Card className="border-l-4 border-l-amber-500" title="">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Active teachers</p>
                      <h3 className="text-2xl font-bold text-slate-900 mt-1">
                        {isLoadingTeachersList ? '...' : totalTeachers}
                      </h3>
                    </div>
                    <div className="rounded-full bg-amber-50 p-3">
                      <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center text-xs text-slate-500 font-medium group-hover:text-amber-600 transition-colors">
                    View Teacher Records →
                  </div>
                </Card>
              </div>

              {/* Teacher Average Attendance */}
              <div onClick={() => navigate('/admin/attendance/teachers')} className="cursor-pointer transition-transform hover:scale-[1.02]">
                <Card className="border-l-4 border-l-purple-500" title="">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-500">{currentMonth} Attendance</p>
                      <div className="flex items-baseline gap-2 mt-1">
                        <h3 className="text-2xl font-bold text-slate-900">
                          {isLoadingTeachers ? '...' : `${getPercentage(teacherSummary?.summary?.present || 0, teacherSummary?.summary?.total_records || 1)}%`}
                        </h3>
                        <span className="text-sm text-slate-500">
                          {isLoadingTeachers ? '...' : `(${teacherSummary?.summary?.present || 0}/${teacherSummary?.summary?.total_records || 0})`}
                        </span>
                      </div>
                    </div>
                    <div className="rounded-full bg-purple-50 p-3">
                      <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center text-xs text-slate-500 font-medium group-hover:text-purple-600 transition-colors">
                    View Teacher Analytics →
                  </div>
                </Card>
              </div>
            </div>
          </div>

        </div>
      </div>
    </DashboardShell>
  );
}
